import type { ReactNode } from 'react'
import type { AnnouncementItem } from '@/lib/market/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { dayFromMs, formatDay, formatEatClock } from '@/lib/market/calendar'

export const formatPublished = (iso: string, withTime = false): string => {
    const ms = Date.parse(iso)
    if (!Number.isFinite(ms)) return ''

    const date = formatDay(dayFromMs(ms))

    return withTime ? `${date}, ${formatEatClock(ms, false)} EAT` : date
}

interface AnnouncementListProps {
    items: AnnouncementItem[]
    compact?: boolean
    className?: string
    emptyText?: string
}

const AnnouncementList = ({ items, compact = false, className, emptyText = 'No announcements have been published yet.' }: AnnouncementListProps): ReactNode => {
    if (!items.length) {
        return (
            <p className='rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground'>
                {emptyText}
            </p>
        )
    }

    return (
        <ul className={cn('divide-y divide-border', className)}>
            {items.map(item => (
                <li key={item.id}>
                    <article className='group relative py-3.5 first:pt-0 last:pb-0'>
                        <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground'>
                            <time dateTime={item.publishedAt} className='tabular-nums'>{formatPublished(item.publishedAt)}</time>

                            {item.category && (
                                <>
                                    <span aria-hidden='true'>&middot;</span>
                                    <span className='font-medium uppercase tracking-[0.06em] text-primary/90' data-mdlfy-cms={`announcements:${item.id}:category`}>
                                        {item.category}
                                    </span>
                                </>
                            )}

                            {item.companyId && item.companyName && (
                                <>
                                    <span aria-hidden='true'>&middot;</span>
                                    <span className='font-mono text-foreground/80'>{item.companyTicker}</span>
                                </>
                            )}
                        </div>

                        <h3 className='mt-1.5 text-sm font-medium leading-snug text-foreground'>
                            <Link href={`/announcements/${item.slug}`} className='after:absolute after:inset-0 group-hover:text-primary'>
                                <span data-mdlfy-cms={`announcements:${item.id}:title`}>{item.title}</span>
                            </Link>
                        </h3>

                        {!compact && item.summary && (
                            <p className='mt-1 line-clamp-2 text-[13px] leading-relaxed text-muted-foreground' data-mdlfy-cms={`announcements:${item.id}:summary`}>
                                {item.summary}
                            </p>
                        )}

                        <ArrowUpRight aria-hidden='true' className='absolute right-0 top-4 size-4 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground' />
                    </article>
                </li>
            ))}
        </ul>
    )
}

export default AnnouncementList
